import { useEffect, useMemo, useState } from "react";
import { Button, message, Space, Switch } from "antd";
import PvTable from "../../components/Table/PvTable";
import ConfirmDelete from "../../components/ConfirmDelete/ConfirmDelete";
import Popup from "../../components/Popup";
import PvForm from "../../components/Form/PvForm";
import useSpectatorAccessService from "../../services/PositionAdmin/spectatorAccessService";
import usePositionService from "../../services/PositionAdmin/positionService";
import useAxios from "../../hooks/useAxios";
import { normalizeEmployeeStatus, isDeanPositionName } from "../../utils/generalUtils";

const SCOPE_LABELS = {
  ao: "AO View (TR/COS/CWA)",
  facsec: "Faculty Secretary View (FSR)",
  both: "Both (TR/COS/CWA + FSR)",
};

const SpectatorAccess = () => {
  const {
    getAllSpectatorAccesses,
    listLoading,
    listResponse,
    addSpectatorAccess,
    updateSpectatorAccess,
    toggleSpectatorAccessNotify,
    removeSpectatorAccess,
  } = useSpectatorAccessService();

  const { getAllApprovers, approverLoading, approverResponse } =
    usePositionService();

  const axiosEmployees = useAxios();

  const [tableData, setTableData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [approverOptions, setApproverOptions] = useState([]);

  useEffect(() => {
    getAllSpectatorAccesses();
    getAllApprovers();
    axiosEmployees.fetchData({ url: `/admin/employee`, method: "GET" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!listResponse?.data) return;

    const mapped = listResponse.data.map((item, index) => ({
      key: index + 1,
      id: item.id,
      employee_id: item.employee_id,
      approver_position_id: item.approver_position_id,
      scope: item.scope,
      notify: item.notify ?? true,
      employee_name: item.employee
        ? [item.employee.first_name, item.employee.middle_name, item.employee.last_name]
            .filter(Boolean)
            .join(" ")
        : `Employee ${item.employee_id}`,
      approver_position: item.approver_position
        ? `${item.approver_position.position_name} - ${item.approver_position.unit}`
        : `Position ${item.approver_position_id}`,
      // Every grant now gets a meaningful scope (Chairs default to facsec,
      // everything else defaults to ao, Deans are explicitly chosen), so
      // it's worth showing on every row, not just Dean ones.
      scope_label: SCOPE_LABELS[item.scope] ?? SCOPE_LABELS.both,
    }));
    setTableData(mapped);
  }, [listResponse]);

  useEffect(() => {
    if (!approverResponse?.data) return;

    setApproverOptions(
      approverResponse.data.map((pos) => ({
        name: pos.id,
        label: `${pos.position_name} - ${pos.unit}`,
      }))
    );
  }, [approverResponse]);

  useEffect(() => {
    if (!axiosEmployees.response?.data) return;

    setEmployeeOptions(
      axiosEmployees.response.data
        .filter((emp) => {
          if (!emp.first_name || !emp.last_name) return false;
          const SUFFIX_RE = /\s+(Jr\.?|Sr\.?|XII|XIII|VIII|VII|VI|IV|III|II|XI|IX|X|V|I)$/i;
          const fullName = [emp.first_name, emp.middle_name, emp.last_name].filter(Boolean).join(" ");
          if (/\d/.test(fullName.replace(SUFFIX_RE, ""))) return false;
          if (normalizeEmployeeStatus(emp).is_active === false) return false;
          return true;
        })
        .map((emp) => ({
          name: emp.id,
          label: [emp.first_name, emp.middle_name, emp.last_name]
            .filter(Boolean)
            .join(" "),
          last_name: emp.last_name ?? "",
        }))
        .sort((a, b) => a.last_name.localeCompare(b.last_name))
    );
  }, [axiosEmployees.response]);

  const handleRemove = async (id) => {
    try {
      await removeSpectatorAccess(id);
      message.success("Spectator access removed.");
      getAllSpectatorAccesses();
    } catch {
      message.error("Failed to remove spectator access.");
    }
  };

  const handleToggleNotify = async (item, notify) => {
    // Optimistic update so the switch doesn't visibly snap back while the
    // request is in flight.
    setTableData((prev) =>
      prev.map((row) => (row.id === item.id ? { ...row, notify } : row))
    );
    try {
      await toggleSpectatorAccessNotify(item.id, notify);
    } catch {
      message.error("Failed to update notification setting.");
      setTableData((prev) =>
        prev.map((row) =>
          row.id === item.id ? { ...row, notify: !notify } : row
        )
      );
    }
  };

  // Keyed by position id so the scope field's shouldShow can look up
  // whether the currently-selected approver position is a Dean.
  const positionById = useMemo(() => {
    const map = {};
    (approverResponse?.data || []).forEach((pos) => {
      map[pos.id] = pos;
    });
    return map;
  }, [approverResponse]);

  const columns = [
    { title: "No.", dataIndex: "key", rowScope: "row", width: 60 },
    { title: "Employee", dataIndex: "employee_name", key: "employee_name" },
    {
      title: "Watches Approver Position",
      dataIndex: "approver_position",
      key: "approver_position",
    },
    {
      title: "Scope",
      dataIndex: "scope_label",
      key: "scope_label",
    },
    {
      title: "Notifications",
      key: "notify",
      // Stops the switch click from also bubbling up to the row click
      // (which opens the edit modal — clicking anywhere else on the row does).
      render: (_, item) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Switch
            checked={item.notify}
            checkedChildren="On"
            unCheckedChildren="Off"
            onChange={(checked) => handleToggleNotify(item, checked)}
            style={{ background: item.notify ? "#00573F" : undefined }}
          />
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      // Stops the delete click from also bubbling up to the row click
      // (which opens the edit modal — clicking anywhere else on the row does).
      render: (_, item) => (
        <div onClick={(e) => e.stopPropagation()}>
          <ConfirmDelete
            isService={true}
            buttonText="Remove"
            onDeleted={() => handleRemove(item.id)}
          />
        </div>
      ),
    },
  ];

  const openEdit = (item) => {
    setEditing(item);
    setIsModalOpen(true);
  };

  const formFields = useMemo(
    () => [
      {
        name: "employee_id",
        label: "Employee",
        type: "select",
        selectItems: employeeOptions,
        searchable: true,
        rules: [{ required: true, message: "Please select an employee." }],
      },
      {
        name: "approver_position_id",
        label: "Approver Position to Watch",
        type: "select",
        selectItems: approverOptions,
        searchable: true,
        rules: [{ required: true, message: "Please select an approver position." }],
      },
      {
        name: "scope",
        label: "Access Scope",
        type: "select",
        selectItems: [
          { name: "ao", label: "AO View (TR/COS/CWA only)" },
          { name: "facsec", label: "Faculty Secretary View (FSR only)" },
          { name: "both", label: "Both (TR/COS/CWA + FSR)" },
        ],
        rules: [{ required: true, message: "Please select an access scope." }],
        // Only a Dean position spans both FSR and TR/COS/CWA — every other
        // approver position is already single-purpose, so this scoping
        // choice is meaningless (and hidden) for them.
        shouldShow: (values) =>
          isDeanPositionName(positionById[values?.approver_position_id]?.position_name),
      },
    ],
    [employeeOptions, approverOptions, positionById]
  );

  return (
    <>
      <Space style={{ marginBottom: 12 }}>
        <Button
          size="large"
          type="primary"
          style={{ background: "#00573F" }}
          onClick={() => {
            setEditing(null);
            setIsModalOpen(true);
          }}
        >
          Add AO Access
        </Button>
      </Space>

      <PvTable
        data={tableData}
        columns={columns}
        loading={listLoading || approverLoading}
        onRowClick={openEdit}
        interactiveRows
        getRowAriaLabel={(record) =>
          `Edit AO access for ${record.employee_name}`
        }
      />

      <Popup
        title={editing ? "Edit AO Access" : "Add AO Access"}
        isOpen={isModalOpen}
        footer={null}
        onCancel={() => {
          setIsModalOpen(false);
          setEditing(null);
        }}
      >
        <PvForm
          isService={true}
          formFields={formFields}
          initialValues={
            editing
              ? {
                  employee_id: editing.employee_id,
                  approver_position_id: editing.approver_position_id,
                  scope: editing.scope,
                }
              : undefined
          }
          onSubmitted={async (values) => {
            try {
              if (editing) {
                await updateSpectatorAccess(
                  editing.id,
                  values.employee_id,
                  values.approver_position_id,
                  values.scope
                );
                message.success("Spectator access updated.");
              } else {
                await addSpectatorAccess(
                  values.employee_id,
                  values.approver_position_id,
                  values.scope
                );
                message.success("Spectator access added.");
              }
              setIsModalOpen(false);
              setEditing(null);
              getAllSpectatorAccesses();
            } catch {
              message.error(
                editing
                  ? "Failed to update spectator access."
                  : "Failed to add spectator access."
              );
            }
          }}
        />
      </Popup>
    </>
  );
};

export default SpectatorAccess;
